import React, { useState, useEffect } from 'react';
import { Download, Upload, Settings, Eye, RefreshCw, CheckCircle, AlertCircle, Clock } from 'lucide-react';
import { LoadingPage } from '../components/LoadingSpinner';
import LoadingSpinner from '../components/LoadingSpinner';

const XMLFeedPage = () => {
  const [loading, setLoading] = useState(false);
  const [syncStatus, setSyncStatus] = useState([]);
  const [config, setConfig] = useState({
    feed_url: '',
    username: '',
    password: '',
    dealer_id: '',
    auto_sync_hours: 6
  });
  const [preview, setPreview] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api/xml-feed`;

  useEffect(() => {
    fetchSyncStatus();
    fetchPreview();
  }, []);

  const fetchSyncStatus = async () => {
    try {
      const response = await fetch(`${API}/status`);
      const data = await response.json();
      setSyncStatus(data.sync_status || []);
    } catch (error) {
      console.error('Error fetching sync status:', error);
    }
  };

  const fetchPreview = async () => {
    try {
      const response = await fetch(`${API}/preview`);
      const data = await response.json();
      setPreview(data);
    } catch (error) {
      console.error('Error fetching preview:', error);
    }
  };

  const handleGenerateXML = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/generate`);
      const blob = await response.blob();
      
      // Download the file
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'commercialmotors_feed.xml';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      alert('XML feed generated and downloaded successfully!');
    } catch (error) {
      console.error('Error generating XML:', error);
      alert('Error generating XML feed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadFeed = async () => {
    if (!config.feed_url || !config.dealer_id) {
      alert('Please configure CommercialMotors.com settings first.');
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`${API}/upload`, { method: 'POST' });
      const data = await response.json();
      
      if (response.ok) {
        alert('Upload initiated! Check status for updates.');
        fetchSyncStatus();
      } else {
        alert(`Upload failed: ${data.detail}`);
      }
    } catch (error) {
      console.error('Error uploading feed:', error);
      alert('Error uploading feed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config)
      });
      
      if (response.ok) {
        alert('Configuration saved successfully!');
      } else {
        alert('Failed to save configuration.');
      }
    } catch (error) {
      console.error('Error saving config:', error);
      alert('Error saving configuration.');
    } finally {
      setLoading(false);
    }
  };

  const StatusIcon = ({ status }) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'failed':
      case 'error':
        return <AlertCircle className="h-5 w-5 text-red-600" />;
      default:
        return <Clock className="h-5 w-5 text-yellow-600" />;
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Eye },
    { id: 'actions', label: 'Actions', icon: RefreshCw },
    { id: 'config', label: 'Configuration', icon: Settings },
  ];

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-black text-white py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl font-black font-inter">
            CommercialMotors.com <span className="text-orange-500">XML Feed</span>
          </h1>
          <p className="text-gray-300 mt-2 font-inter">
            Manage your truck inventory synchronization with CommercialMotors.com
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-lg p-4">
              <h3 className="font-bold text-gray-800 mb-4 font-inter">XML Feed Management</h3>
              <nav className="space-y-1">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                        activeTab === tab.id
                          ? 'bg-orange-600 text-white'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      <span className="font-inter text-sm">{tab.label}</span>
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-lg shadow-lg p-8">
              
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-black text-black font-inter">Feed Overview</h2>
                  
                  {/* Stats */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                      <h3 className="font-bold text-orange-800 font-inter">Total Vehicles</h3>
                      <p className="text-2xl font-bold text-orange-600">
                        {preview?.total_vehicles || 0}
                      </p>
                    </div>
                    
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <h3 className="font-bold text-green-800 font-inter">Last Upload</h3>
                      <p className="text-sm text-green-600">
                        {syncStatus.find(s => s.type === 'upload')?.timestamp 
                          ? new Date(syncStatus.find(s => s.type === 'upload').timestamp).toLocaleDateString()
                          : 'Never'
                        }
                      </p>
                    </div>
                    
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h3 className="font-bold text-blue-800 font-inter">Status</h3>
                      <div className="flex items-center gap-2">
                        <StatusIcon status={syncStatus.find(s => s.type === 'upload')?.status} />
                        <p className="text-sm text-blue-600 capitalize">
                          {syncStatus.find(s => s.type === 'upload')?.status || 'Unknown'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Vehicle Preview */}
                  {preview && (
                    <div>
                      <h3 className="text-lg font-bold text-black mb-4 font-inter">Vehicle Preview</h3>
                      <div className="overflow-x-auto">
                        <table className="min-w-full border border-gray-200 rounded-lg">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-2 text-left text-sm font-bold text-gray-700">Stock ID</th>
                              <th className="px-4 py-2 text-left text-sm font-bold text-gray-700">Title</th>
                              <th className="px-4 py-2 text-left text-sm font-bold text-gray-700">Make/Model</th>
                              <th className="px-4 py-2 text-left text-sm font-bold text-gray-700">Year</th>
                              <th className="px-4 py-2 text-left text-sm font-bold text-gray-700">Images</th>
                            </tr>
                          </thead>
                          <tbody>
                            {preview.preview_vehicles.map((vehicle, index) => (
                              <tr key={index} className="border-t">
                                <td className="px-4 py-2 text-sm font-mono">{vehicle.stock_number}</td>
                                <td className="px-4 py-2 text-sm">{vehicle.title}</td>
                                <td className="px-4 py-2 text-sm">{vehicle.make} {vehicle.model}</td>
                                <td className="px-4 py-2 text-sm">{vehicle.year}</td>
                                <td className="px-4 py-2 text-sm">{vehicle.images_count} images</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Sync Status */}
                  {syncStatus.length > 0 && (
                    <div>
                      <h3 className="text-lg font-bold text-black mb-4 font-inter">Recent Activity</h3>
                      <div className="space-y-2">
                        {syncStatus.map((status, index) => (
                          <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                            <StatusIcon status={status.status} />
                            <div className="flex-1">
                              <p className="font-medium capitalize">{status.type} Operation</p>
                              <p className="text-sm text-gray-600">{status.message}</p>
                              <p className="text-xs text-gray-500">
                                {new Date(status.timestamp).toLocaleString()}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Actions Tab */}
              {activeTab === 'actions' && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-black text-black font-inter">Feed Actions</h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Generate XML */}
                    <div className="border border-gray-200 rounded-lg p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <Download className="h-6 w-6 text-orange-600" />
                        <h3 className="text-lg font-bold font-inter">Generate XML Feed</h3>
                      </div>
                      <p className="text-gray-600 mb-4 font-inter">
                        Download an XML file containing all your truck inventory for manual upload to CommercialMotors.com.
                      </p>
                      <button
                        onClick={handleGenerateXML}
                        disabled={loading}
                        className="w-full bg-orange-600 hover:bg-orange-700 disabled:bg-orange-400 text-white px-4 py-2 rounded-lg font-bold flex items-center justify-center gap-2"
                      >
                        {loading ? <LoadingSpinner size="sm" /> : <Download className="h-4 w-4" />}
                        Generate & Download
                      </button>
                    </div>

                    {/* Upload Feed */}
                    <div className="border border-gray-200 rounded-lg p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <Upload className="h-6 w-6 text-green-600" />
                        <h3 className="text-lg font-bold font-inter">Upload to CommercialMotors</h3>
                      </div>
                      <p className="text-gray-600 mb-4 font-inter">
                        Automatically upload your current inventory directly to CommercialMotors.com via their API.
                      </p>
                      <button
                        onClick={handleUploadFeed}
                        disabled={loading || !config.feed_url}
                        className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg font-bold flex items-center justify-center gap-2"
                      >
                        {loading ? <LoadingSpinner size="sm" /> : <Upload className="h-4 w-4" />}
                        Upload Now
                      </button>
                      {!config.feed_url && (
                        <p className="text-xs text-red-600 mt-2">Configure settings first</p>
                      )}
                    </div>
                  </div>

                  {/* Instructions */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                    <h3 className="font-bold text-blue-800 mb-3 font-inter">How It Works</h3>
                    <ol className="list-decimal list-inside space-y-2 text-blue-700 font-inter">
                      <li>Your truck inventory is automatically formatted according to CommercialMotors.com specifications</li>
                      <li>The XML includes all required fields: stock number, make, model, body type, description, images</li>
                      <li>Optional fields like price, mileage, power, weight are included when available</li>
                      <li>Contact information (Lawman Commercials details) is automatically added</li>
                      <li>You can either download the XML file for manual upload or use automatic upload</li>
                    </ol>
                  </div>
                </div>
              )}

              {/* Configuration Tab */}
              {activeTab === 'config' && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-black text-black font-inter">CommercialMotors.com Configuration</h2>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2 font-inter">
                        Feed Upload URL
                      </label>
                      <input
                        type="url"
                        value={config.feed_url}
                        onChange={(e) => setConfig({ ...config, feed_url: e.target.value })}
                        placeholder="https://api.commercialmotors.com/feed/upload"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent font-inter"
                      />
                      <p className="text-xs text-gray-600 mt-1">URL provided by CommercialMotors.com for feed uploads</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2 font-inter">
                          Username
                        </label>
                        <input
                          type="text"
                          value={config.username}
                          onChange={(e) => setConfig({ ...config, username: e.target.value })}
                          placeholder="Your CM username"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent font-inter"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2 font-inter">
                          Password
                        </label>
                        <input
                          type="password"
                          value={config.password}
                          onChange={(e) => setConfig({ ...config, password: e.target.value })}
                          placeholder="Your CM password"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent font-inter"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2 font-inter">
                        Dealer ID
                      </label>
                      <input
                        type="text"
                        value={config.dealer_id}
                        onChange={(e) => setConfig({ ...config, dealer_id: e.target.value })}
                        placeholder="Your dealer ID from CommercialMotors.com"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent font-inter"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2 font-inter">
                        Auto-Sync Interval (hours)
                      </label>
                      <select
                        value={config.auto_sync_hours}
                        onChange={(e) => setConfig({ ...config, auto_sync_hours: parseInt(e.target.value) })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent font-inter"
                      >
                        <option value="1">Every Hour</option>
                        <option value="6">Every 6 Hours</option>
                        <option value="12">Every 12 Hours</option>
                        <option value="24">Daily</option>
                      </select>
                    </div>

                    <button
                      onClick={handleSaveConfig}
                      disabled={loading}
                      className="bg-orange-600 hover:bg-orange-700 disabled:bg-orange-400 text-white px-6 py-3 rounded-lg font-bold flex items-center gap-2"
                    >
                      {loading ? <LoadingSpinner size="sm" /> : <Settings className="h-4 w-4" />}
                      Save Configuration
                    </button>
                  </div>

                  {/* Help Section */}
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                    <h3 className="font-bold text-yellow-800 mb-3 font-inter">Need Help?</h3>
                    <div className="text-yellow-700 font-inter space-y-2">
                      <p><strong>Where to get these details:</strong></p>
                      <ul className="list-disc list-inside ml-4 space-y-1">
                        <li>Contact CommercialMotors.com support for your feed upload URL</li>
                        <li>Use your existing CommercialMotors.com account username/password</li>
                        <li>Your dealer ID should be provided when you sign up for feed integration</li>
                      </ul>
                      <p className="mt-3"><strong>Contact:</strong> support@commercialmotors.com</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default XMLFeedPage;